import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, GripVertical, Copy, Download, ChevronDown, ChevronRight, Loader2, Check, X } from 'lucide-react';

function GateRow({ gate, index, onUpdate, onRemove, isPostOrder }) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 rounded group hover:opacity-90 transition-colors" style={{ background: 'var(--card-bg-alt)' }}>
      <GripVertical className="w-3.5 h-3.5 cursor-grab opacity-40" />
      <span className="w-6 text-center text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{index + 1}</span>
      <input value={gate.stageGateName} onChange={e => onUpdate({ ...gate, stageGateName: e.target.value })}
        className="flex-1 px-2 py-1 rounded text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
        placeholder="Gate name" />
      <input type="number" value={gate.weightagePercent} onChange={e => onUpdate({ ...gate, weightagePercent: parseFloat(e.target.value) || 0 })}
        className="w-16 px-2 py-1 rounded text-sm text-center border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
        placeholder="Wt%" />
      <label className="flex items-center gap-1 text-xs cursor-pointer" style={{ color: 'var(--text-muted)' }}>
        <input type="checkbox" checked={gate.isMilestone || false} onChange={e => onUpdate({ ...gate, isMilestone: e.target.checked })} className="w-3.5 h-3.5" />
        MS
      </label>
      <label className="flex items-center gap-1 text-xs cursor-pointer" style={{ color: 'var(--text-muted)' }}>
        <input type="checkbox" checked={gate.isPhaseTerminal || false} onChange={e => onUpdate({ ...gate, isPhaseTerminal: e.target.checked })} className="w-3.5 h-3.5" />
        Term
      </label>
      {isPostOrder && (
        <label className="flex items-center gap-1 text-xs cursor-pointer" style={{ color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={gate.inspectionRequired || false} onChange={e => onUpdate({ ...gate, inspectionRequired: e.target.checked })} className="w-3.5 h-3.5" />
          Insp
        </label>
      )}
      <button onClick={onRemove} className="p-1 rounded hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 className="w-3.5 h-3.5 text-red-400" />
      </button>
    </div>
  );
}

function ConfigEditor({ config, projectId, materialTypes, onSaved, onCancel }) {
  const isNew = !config?.id;
  const [preOrderWt, setPreOrderWt] = useState(config?.preOrderWeightagePercent || 30);
  const [postOrderWt, setPostOrderWt] = useState(config?.postOrderWeightagePercent || 70);
  const [preGates, setPreGates] = useState(config?.preOrderStageGates || []);
  const [postGates, setPostGates] = useState(config?.postOrderStageGates || []);
  const [materialTypeCode, setMaterialTypeCode] = useState(config?.materialTypeCode || '');
  const [materialTypeName, setMaterialTypeName] = useState(config?.materialTypeName || '');
  const [materialCategory, setMaterialCategory] = useState(config?.materialCategory || 'TAGGED');
  const [saving, setSaving] = useState(false);

  const preSum = preGates.reduce((s, g) => s + (g.weightagePercent || 0), 0);
  const postSum = postGates.reduce((s, g) => s + (g.weightagePercent || 0), 0);
  const phaseSum = preOrderWt + postOrderWt;

  const addGate = (phase) => {
    const gates = phase === 'pre' ? preGates : postGates;
    const setGates = phase === 'pre' ? setPreGates : setPostGates;
    const prefix = phase === 'pre' ? 'PRE' : 'POST';
    setGates([...gates, {
      stageGateId: `${prefix}-${String(gates.length + 1).padStart(2, '0')}`,
      stageGateName: '', sequenceOrder: gates.length + 1, weightagePercent: 0,
      isMilestone: false, isPhaseTerminal: false, inspectionRequired: false
    }]);
  };

  const loadSuggested = () => {
    const mt = materialTypes.find(t => t.materialTypeCode === materialTypeCode);
    if (!mt) { toast.error('Select a material type first'); return; }
    const pre = (mt.suggestedPreOrderGates || []).map((name, i) => ({
      stageGateId: `PRE-${String(i + 1).padStart(2, '0')}`, stageGateName: name, sequenceOrder: i + 1,
      weightagePercent: Math.round(100 / mt.suggestedPreOrderGates.length),
      isMilestone: i === mt.suggestedPreOrderGates.length - 1,
      isPhaseTerminal: i === mt.suggestedPreOrderGates.length - 1
    }));
    const post = (mt.suggestedPostOrderGates || []).map((name, i) => ({
      stageGateId: `POST-${String(i + 1).padStart(2, '0')}`, stageGateName: name, sequenceOrder: i + 1,
      weightagePercent: Math.round(100 / mt.suggestedPostOrderGates.length),
      isMilestone: false, isPhaseTerminal: i === mt.suggestedPostOrderGates.length - 1,
      inspectionRequired: name.toLowerCase().includes('inspect') || name.toLowerCase().includes('fat') || name.toLowerCase().includes('test')
    }));
    // Adjust last gate weightage to make sum = 100
    if (pre.length) pre[pre.length - 1].weightagePercent += (100 - pre.reduce((s, g) => s + g.weightagePercent, 0));
    if (post.length) post[post.length - 1].weightagePercent += (100 - post.reduce((s, g) => s + g.weightagePercent, 0));
    setPreGates(pre);
    setPostGates(post);
    setMaterialCategory(mt.materialCategory);
    setMaterialTypeName(mt.materialTypeName);
    toast.success('Loaded suggested gates');
  };

  const handleSave = async () => {
    if (phaseSum !== 100) { toast.error('Phase split must equal 100%'); return; }
    if (Math.abs(preSum - 100) > 0.5) { toast.error(`Pre-order gates must sum to 100% (current: ${preSum}%)`); return; }
    if (Math.abs(postSum - 100) > 0.5) { toast.error(`Post-order gates must sum to 100% (current: ${postSum}%)`); return; }
    if (!materialTypeCode) { toast.error('Material type code required'); return; }

    setSaving(true);
    const payload = {
      materialCategory, materialTypeCode, materialTypeName,
      preOrderWeightagePercent: preOrderWt, postOrderWeightagePercent: postOrderWt,
      preOrderStageGates: preGates.map((g, i) => ({ ...g, sequenceOrder: i + 1, stageGateId: `PRE-${String(i + 1).padStart(2, '0')}` })),
      postOrderStageGates: postGates.map((g, i) => ({ ...g, sequenceOrder: i + 1, stageGateId: `POST-${String(i + 1).padStart(2, '0')}` }))
    };
    try {
      if (isNew) {
        await api.post(`/v1/projects/${projectId}/mci/stage-gate-configs`, payload);
      } else {
        await api.put(`/v1/projects/${projectId}/mci/stage-gate-configs/${config.id}`, payload);
      }
      toast.success(isNew ? 'Configuration created' : 'Configuration updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="p-4 rounded-lg border space-y-4" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }} data-testid="stage-gate-editor">
      {/* Material type selector */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Material Type Code</label>
          <select value={materialTypeCode} onChange={e => {
            setMaterialTypeCode(e.target.value);
            const mt = materialTypes.find(t => t.materialTypeCode === e.target.value);
            if (mt) { setMaterialTypeName(mt.materialTypeName); setMaterialCategory(mt.materialCategory); }
          }} disabled={!isNew}
            className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">Select...</option>
            {materialTypes.map(t => <option key={t.materialTypeCode} value={t.materialTypeCode}>{t.materialTypeCode} - {t.materialTypeName}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Category</label>
          <div className="px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>{materialCategory}</div>
        </div>
        <div className="flex items-end">
          <Button size="sm" variant="outline" onClick={loadSuggested}><Download className="w-3.5 h-3.5 mr-1" /> Load Suggested</Button>
        </div>
      </div>

      {/* Phase split */}
      <div className="p-3 rounded-lg" style={{ background: 'var(--card-bg-alt)' }}>
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Phase Split:</span>
          <label className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-muted)' }}>
            Pre-Order
            <input type="number" value={preOrderWt} onChange={e => { const v = parseFloat(e.target.value) || 0; setPreOrderWt(v); setPostOrderWt(100 - v); }}
              className="w-16 px-2 py-1 rounded text-center border text-sm" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />%
          </label>
          <span style={{ color: 'var(--text-muted)' }}>+</span>
          <label className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-muted)' }}>
            Post-Order
            <input type="number" value={postOrderWt} onChange={e => { const v = parseFloat(e.target.value) || 0; setPostOrderWt(v); setPreOrderWt(100 - v); }}
              className="w-16 px-2 py-1 rounded text-center border text-sm" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />%
          </label>
          <span className="text-sm" style={{ color: phaseSum === 100 ? '#10B981' : '#EF4444' }}>= {phaseSum}% {phaseSum === 100 ? <Check className="w-4 h-4 inline" /> : <X className="w-4 h-4 inline" />}</span>
        </div>
      </div>

      {/* Two-phase gate editors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pre-Order */}
        <div className="space-y-2 p-3 rounded-lg border" style={{ borderColor: 'rgba(94,234,212,0.3)', background: 'rgba(94,234,212,0.03)' }}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold" style={{ color: '#5EEAD4' }}>PRE-ORDER GATES ({preOrderWt}%)</h4>
            <span className="text-xs" style={{ color: Math.abs(preSum - 100) <= 0.5 ? '#10B981' : '#EF4444' }}>Sum: {preSum}%</span>
          </div>
          <div className="space-y-1">
            {preGates.map((g, i) => (
              <GateRow key={i} gate={g} index={i}
                onUpdate={updated => setPreGates(prev => prev.map((p, j) => j === i ? updated : p))}
                onRemove={() => setPreGates(prev => prev.filter((_, j) => j !== i))}
                isPostOrder={false} />
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => addGate('pre')} className="w-full" data-testid="add-pre-gate">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Pre-Order Gate
          </Button>
        </div>

        {/* Post-Order */}
        <div className="space-y-2 p-3 rounded-lg border" style={{ borderColor: 'rgba(196,168,255,0.3)', background: 'rgba(196,168,255,0.03)' }}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold" style={{ color: '#C4A8FF' }}>POST-ORDER GATES ({postOrderWt}%)</h4>
            <span className="text-xs" style={{ color: Math.abs(postSum - 100) <= 0.5 ? '#10B981' : '#EF4444' }}>Sum: {postSum}%</span>
          </div>
          <div className="space-y-1">
            {postGates.map((g, i) => (
              <GateRow key={i} gate={g} index={i}
                onUpdate={updated => setPostGates(prev => prev.map((p, j) => j === i ? updated : p))}
                onRemove={() => setPostGates(prev => prev.filter((_, j) => j !== i))}
                isPostOrder={true} />
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => addGate('post')} className="w-full" data-testid="add-post-gate">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Post-Order Gate
          </Button>
        </div>
      </div>

      {/* Effective weightage preview */}
      {preGates.length > 0 && postGates.length > 0 && (
        <div className="p-3 rounded-lg text-xs" style={{ background: 'var(--card-bg-alt)' }}>
          <div className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Effective Weightage Preview</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-0.5">
              {preGates.filter(g => g.stageGateName).map((g, i) => (
                <div key={i} style={{ color: 'var(--text-muted)' }}>{g.stageGateName}: {preOrderWt}% x {g.weightagePercent}% = <span style={{ color: 'var(--accent-primary)' }}>{((preOrderWt * g.weightagePercent) / 100).toFixed(1)}%</span></div>
              ))}
            </div>
            <div className="space-y-0.5">
              {postGates.filter(g => g.stageGateName).map((g, i) => (
                <div key={i} style={{ color: 'var(--text-muted)' }}>{g.stageGateName}: {postOrderWt}% x {g.weightagePercent}% = <span style={{ color: 'var(--accent-primary)' }}>{((postOrderWt * g.weightagePercent) / 100).toFixed(1)}%</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{isNew ? 'Create' : 'Update'} Configuration</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function MCIStageGateConfig({ projectId }) {
  const [configs, setConfigs] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState('ALL');

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = filter !== 'ALL' ? `?category=${filter}` : '';
      const [cfgRes, mtRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mci/stage-gate-configs${params}`),
        api.get(`/v1/projects/${projectId}/mci/material-types`)
      ]);
      setConfigs(cfgRes.data);
      setMaterialTypes(mtRes.data);
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  }, [projectId, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this config?')) return;
    try { await api.delete(`/v1/projects/${projectId}/mci/stage-gate-configs/${id}`); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const filtered = filter === 'ALL' ? configs : configs.filter(c => c.materialCategory === filter);

  return (
    <div className="space-y-4" data-testid="stage-gate-config-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Stage Gate Configurations</h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
            {['ALL', 'BULK', 'TAGGED'].map(f => (
              <button key={f} onClick={() => setFilter(f)} className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={{ background: filter === f ? 'var(--accent-primary)' : 'var(--card-bg)', color: filter === f ? '#fff' : 'var(--text-muted)' }}>
                {f}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => { setShowNew(true); setExpanded(null); }} data-testid="new-config-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Config
          </Button>
        </div>
      </div>

      {showNew && (
        <ConfigEditor projectId={projectId} materialTypes={materialTypes}
          onSaved={() => { setShowNew(false); fetchData(); }} onCancel={() => setShowNew(false)} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>
      ) : (
        <div className="space-y-2">
          {filtered.map(cfg => (
            <div key={cfg.id} className="rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setExpanded(expanded === cfg.id ? null : cfg.id)}
                style={{ background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-3">
                  {expanded === cfg.id ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /> : <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                  <span className="font-mono text-sm font-medium" style={{ color: 'var(--accent-primary)' }}>{cfg.materialTypeCode}</span>
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{cfg.materialTypeName}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs" style={{
                    background: cfg.materialCategory === 'BULK' ? 'rgba(94,234,212,0.15)' : 'rgba(196,168,255,0.15)',
                    color: cfg.materialCategory === 'BULK' ? '#5EEAD4' : '#C4A8FF'
                  }}>{cfg.materialCategory}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Pre: {cfg.preOrderWeightagePercent}% | Post: {cfg.postOrderWeightagePercent}%</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{(cfg.preOrderStageGates || []).length + (cfg.postOrderStageGates || []).length} gates</span>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(cfg.id); }} className="p-1 rounded hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
              {expanded === cfg.id && (
                <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <ConfigEditor config={cfg} projectId={projectId} materialTypes={materialTypes}
                    onSaved={() => { setExpanded(null); fetchData(); }} onCancel={() => setExpanded(null)} />
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && !showNew && (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No configurations found. Create one to start tracking procurement.</div>
          )}
        </div>
      )}
    </div>
  );
}
